import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  TextField,
  Button,
  Checkbox,
  FormControlLabel,
  Divider,
  Chip,
} from "@mui/material";
import AssigneeDropdown from "../../components/AssigneeDropdown";
import StatusDropdown from "../../components/StatusDropdown";
import ImportanceDropdown from "../../components/ImportanceDropdown";

const BASE_URL = process.env.REACT_APP_BASE_URL ?? "";

const TicketDetailModal = ({ open, ticket, onClose, onUpdate }) => {
  const [localTicket, setLocalTicket] = useState(null);
  const [responseText, setResponseText] = useState("");
  const [internalNote, setInternalNote] = useState(false);

  useEffect(() => {
    const fetchTicket = async () => {
      if (!ticket?.id) return;
      try {
        const res = await fetch(`${BASE_URL}/api/tickets/${ticket.id}`, {
          credentials: "include",
        });
        const fullTicket = await res.json();
        setLocalTicket(fullTicket);
      } catch (error) {
        console.error("Error fetching full ticket:", error);
      }
    };

    fetchTicket();
  }, [ticket]);

  if (!ticket || !localTicket) return null;

  const handleFieldChange = (field, value) => {
    setLocalTicket((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSave = async () => {
    const ticketFields = {
      assigned_to: localTicket.assigned_to,
      status: localTicket.status,
      importance: localTicket.importance,
    };

    const updatedTicket = await onUpdate({ id: localTicket.id, ticketFields }, true);
    if (updatedTicket) {
      setLocalTicket(updatedTicket);
    }
  };

  const handleSendResponse = async () => {
    if (!responseText.trim()) return;

    const responseData = {
      text: responseText,
      internal: internalNote,
    };

    const updatedTicket = await onUpdate(
      {
        id: localTicket.id,
        responseData,
      },
      false
    );

    setResponseText("");
    setInternalNote(false);

    if (updatedTicket) {
      setLocalTicket(updatedTicket);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{`Ticket: ${localTicket.email_subject}`}</DialogTitle>
      <DialogContent
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "85vh",
          p: 0,
        }}
      >
        <Box sx={{ padding: 3, flexShrink: 0 }}>
          <Box display="flex" justifyContent="space-between" mb={2}>
            <Typography variant="body2">
              <strong>Sender:</strong> {localTicket.email_sender}
            </Typography>
            <Typography variant="body2">
              <strong>Created:</strong>{" "}
              {new Date(localTicket.created_at).toLocaleString("en-US")}
            </Typography>
          </Box>

          <Box display="flex" gap={3} mb={2}>
            <Box flex={3}>
              <Box
                sx={{
                  backgroundColor: "#cde3f3",
                  color: "#1a1a1a",
                  p: 2,
                  borderRadius: 1,
                  minHeight: 120,
                  "& *": {
                    backgroundColor: "transparent !important",
                  },
                }}
              >
                <div
                  dangerouslySetInnerHTML={{ __html: localTicket.email_body }}
                  style={{ all: "unset", color: "#1a1a1a" }}
                />
              </Box>

              <Box mt={1}>
                {localTicket.attachments?.length > 0 ? (
                  localTicket.attachments.map((file, idx) => (
                    <Typography key={idx}>
                      <a href={file.url} target="_blank" rel="noreferrer">
                        {file.name}
                      </a>
                    </Typography>
                  ))
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No attachments
                  </Typography>
                )}
              </Box>
            </Box>

            <Box flex={1} display="flex" flexDirection="column" gap={2}>
              <AssigneeDropdown
                ticket={localTicket}
                onChange={(val) => handleFieldChange("assigned_to", val)}
              />
              <StatusDropdown
                ticket={localTicket}
                onChange={(val) => handleFieldChange("status", val)}
              />
              <ImportanceDropdown
                ticketId={localTicket.id}
                currentImportance={localTicket.importance}
                onChange={(val) => handleFieldChange("importance", val)}
              />
            </Box>
          </Box>
        </Box>

        <Divider />

        <Box sx={{ padding: 3, flexShrink: 0 }}>
          <Typography variant="h6" mb={1}>
            Responses
          </Typography>
          <TextField
            fullWidth
            multiline
            minRows={4}
            value={responseText}
            onChange={(e) => setResponseText(e.target.value)}
            placeholder="Write a reply or internal note..."
            sx={{
              "& .MuiOutlinedInput-root": {
                "&.Mui-focused fieldset": {
                  borderColor: internalNote ? "#fdd835" : "#187089",
                  borderWidth: 2,
                },
              },
            }}
          />
          <Box display="flex" alignItems="center" justifyContent="space-between" mt={1}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={internalNote}
                  onChange={(e) => setInternalNote(e.target.checked)}
                  sx={{
                    "&.Mui-checked": {
                      color: "#187089",
                    },
                  }}
                />
              }
              label="Internal note"
            />
            <Button variant="contained" onClick={handleSendResponse}>
              Send
            </Button>
          </Box>
        </Box>

        <Divider />

        <Box
          sx={{
            padding: 3,
            flexGrow: 1,
            overflowY: "auto",
            minHeight: 0,
          }}
        >
          <Typography variant="h6" mb={2}>
            Response History
          </Typography>

          {localTicket.responses?.length > 0 ? (
            localTicket.responses.map((resp, idx) => {
              const isInternal = resp.is_internal;
              const isFromInternalDomain = resp.responder_email?.endsWith("@kbswebdesign.com");
              const isFromCustomer = !isInternal && !isFromInternalDomain;

              let bgColor = "#e8f0fe";
              let textColor = "#1a1a1a";

              if (isInternal) {
                bgColor = "#fff9c4";
                textColor = "#333333";
              } else if (isFromCustomer) {
                bgColor = "#dbf0d5";
              }

              return (
                <Box
                  key={idx}
                  mb={3}
                  p={2}
                  sx={{
                    backgroundColor: bgColor,
                    color: textColor,
                    borderRadius: 1,
                    border: "1px solid #ccc",
                  }}
                >
                  <Box display="flex" justifyContent="space-between" mb={1}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography variant="body2">
                        <strong>From:</strong> {resp.responder_email || "Unknown"}
                      </Typography>
                      {isInternal && (
                        <Chip
                          label="internal note"
                          size="small"
                          sx={{
                            backgroundColor: "#fdd835",
                            color: "#000",
                            fontWeight: "bold",
                            textTransform: "uppercase",
                          }}
                        />
                      )}
                    </Box>
                    <Typography variant="body2">
                      <strong>Date:</strong>{" "}
                      {new Date(resp.created_at).toLocaleString("en-US")}
                    </Typography>
                  </Box>
                  <div
                    dangerouslySetInnerHTML={{ __html: resp.message_body }}
                    style={{
                      all: "unset",
                      color: textColor,
                      fontSize: "1rem",
                      lineHeight: 1.5,
                    }}
                  />
                  {resp.attachments?.length > 0 && (
                    <Box mt={1}>
                      <Typography variant="body2" fontWeight="bold">
                        Attachments:
                      </Typography>
                      {resp.attachments.map((file, i) => (
                        <Typography key={i}>
                          <a href={file.url} target="_blank" rel="noreferrer">
                            {file.name}
                          </a>
                        </Typography>
                      ))}
                    </Box>
                  )}
                </Box>
              );
            })
          ) : (
            <Typography variant="body2" color="text.secondary">
              No previous responses
            </Typography>
          )}
        </Box>

        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 2,
            padding: 3,
            borderTop: "1px solid #ccc",
            mt: "auto",
            flexShrink: 0,
          }}
        >
          <Button
            onClick={onClose}
            sx={{
              color: (theme) =>
                theme.palette.mode === "dark" ? "#fff" : "inherit",
            }}
          >
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSave}>
            Save
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default TicketDetailModal;